/**
 * De teksten van de kiosk (Stap 40, KIO-2), in de drie talen van de app.
 *
 * Een eigen woordenboek, zoals de zelfregistratie: de kiosk draait buiten de app-schil en zonder
 * aangemelde gebruiker, en de taalchips op het rustscherm gelden alleen voor de volgende aanmelding.
 * Wie afmeldt, laat ook zijn taalkeuze niet achter.
 */

export const KIOSK_LANGUAGES = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
] as const;
export type KioskLanguage = (typeof KIOSK_LANGUAGES)[number]['code'];

export interface KioskTexts {
  kiosk: (name: string) => string;
  signIn: string;
  orBadge: string;
  offline: string;
  typeNumber: string;
  typeNumberHint: string;
  cancel: string;
  next: string;
  typePin: string;
  typePinHint: string;
  hello: (name: string) => string;
  signOut: string;
  toConfirm: string;
  allDone: string;
  readAndConfirm: string;
  recent: string;
  documents: string;
  back: string;
  postOf: (index: number, total: number) => string;
  view: string;
  confirm: string;
  confirmedAt: (time: string) => string;
  confirmedHint: string;
  stillThere: string;
  signOutIn: (seconds: number) => string;
  imHere: string;
  signedOut: string;
  nothingLeft: string;
  backIn: (seconds: number) => string;
  thirtySeconds: string;
  errors: { invalid: string; locked: (minutes: number) => string; notActivated: string };
  revoked: string;
  revokedHint: string;
  setupTitle: string;
  setupHint: string;
  setupCode: string;
  setup: string;
  setupFailed: string;
  confirmFailed: string;
  ready: (name: string) => string;
  readyHint: string;
  setupAgain: string;
  close: string;
  readDocument: string;
}

const NL: KioskTexts = {
  kiosk: (name) => `Kiosk ${name}`,
  signIn: 'Aanmelden',
  orBadge: 'of scan je badge',
  offline: 'Geen verbinding. Aanmelden kan nu niet.',
  typeNumber: 'Typ je personeelsnummer',
  typeNumberHint: 'Het nummer staat op je badge.',
  cancel: 'Annuleren',
  next: 'Volgende',
  typePin: 'Typ je PIN',
  typePinHint: 'Zes cijfers. Niemand kijkt mee.',
  hello: (name) => `Hallo ${name}`,
  signOut: 'Afmelden',
  toConfirm: 'Te bevestigen',
  allDone: 'Je bent helemaal bij.',
  readAndConfirm: 'Lees en bevestig',
  recent: 'Recent',
  documents: 'Documenten om te lezen',
  back: 'Terug',
  postOf: (index, total) => `bericht ${index} van ${total}`,
  view: 'Bekijk',
  confirm: 'Bevestig',
  confirmedAt: (time) => `Bevestigd om ${time}`,
  confirmedHint: 'Dank je. Je bevestiging staat genoteerd.',
  stillThere: 'Ben je er nog?',
  signOutIn: (seconds) => `Je wordt over ${seconds} seconden afgemeld.`,
  imHere: 'Ik ben er nog',
  signedOut: 'Je bent afgemeld',
  nothingLeft: 'Er staat niets meer van jou op dit toestel.',
  backIn: (seconds) => `Terug naar het beginscherm over ${seconds} seconden`,
  thirtySeconds: 'Nog 30 seconden. Bevestig wat je open hebt staan.',
  errors: {
    invalid: 'Personeelsnummer of PIN klopt niet. Probeer het opnieuw.',
    locked: (minutes) =>
      `Te veel pogingen. Probeer over ${minutes} minuten opnieuw of vraag je ploegleider.`,
    notActivated: 'Je account is nog niet geactiveerd. Vraag HR om een activatiecode.',
  },
  revoked: 'Deze kiosk is uitgeschakeld.',
  revokedHint: 'Vraag HR om dit toestel opnieuw in te richten.',
  setupTitle: 'Dit toestel is nog geen kiosk',
  setupHint: 'Typ de code van acht tekens die je in het beheer kreeg.',
  setupCode: 'Code',
  setup: 'Inrichten',
  setupFailed: 'Deze code klopt niet of is verlopen.',
  confirmFailed: 'Bevestigen is niet gelukt. Probeer het opnieuw.',
  ready: (name) => `Kiosk ${name} is klaar`,
  readyHint: 'Medewerkers kunnen zich nu aanmelden.',
  setupAgain: 'Opnieuw inrichten',
  close: 'Sluiten',
  readDocument: 'Lees',
};

const FR: KioskTexts = {
  kiosk: (name) => `Borne ${name}`,
  signIn: 'Se connecter',
  orBadge: 'ou scannez votre badge',
  offline: 'Pas de connexion. Impossible de se connecter pour le moment.',
  typeNumber: 'Tapez votre numéro de personnel',
  typeNumberHint: 'Le numéro figure sur votre badge.',
  cancel: 'Annuler',
  next: 'Suivant',
  typePin: 'Tapez votre code PIN',
  typePinHint: 'Six chiffres. Personne ne regarde.',
  hello: (name) => `Bonjour ${name}`,
  signOut: 'Se déconnecter',
  toConfirm: 'À confirmer',
  allDone: 'Vous êtes à jour.',
  readAndConfirm: 'Lire et confirmer',
  recent: 'Récent',
  documents: 'Documents à lire',
  back: 'Retour',
  postOf: (index, total) => `message ${index} sur ${total}`,
  view: 'Voir',
  confirm: 'Confirmer',
  confirmedAt: (time) => `Confirmé à ${time}`,
  confirmedHint: 'Merci. Votre confirmation est enregistrée.',
  stillThere: 'Êtes-vous toujours là ?',
  signOutIn: (seconds) => `Vous serez déconnecté dans ${seconds} secondes.`,
  imHere: 'Je suis là',
  signedOut: 'Vous êtes déconnecté',
  nothingLeft: 'Rien de vous ne reste sur cet appareil.',
  backIn: (seconds) => `Retour à l'écran d'accueil dans ${seconds} secondes`,
  thirtySeconds: 'Encore 30 secondes. Confirmez ce que vous avez ouvert.',
  errors: {
    invalid: 'Numéro de personnel ou code PIN incorrect. Réessayez.',
    locked: (minutes) =>
      `Trop de tentatives. Réessayez dans ${minutes} minutes ou demandez à votre chef d'équipe.`,
    notActivated: "Votre compte n'est pas encore activé. Demandez un code d'activation aux RH.",
  },
  revoked: 'Cette borne est désactivée.',
  revokedHint: 'Demandez aux RH de reconfigurer cet appareil.',
  setupTitle: "Cet appareil n'est pas encore une borne",
  setupHint: "Tapez le code de huit caractères reçu dans l'administration.",
  setupCode: 'Code',
  setup: 'Configurer',
  setupFailed: "Ce code n'est pas correct ou a expiré.",
  confirmFailed: "La confirmation n'a pas abouti. Réessayez.",
  ready: (name) => `La borne ${name} est prête`,
  readyHint: 'Les collaborateurs peuvent maintenant se connecter.',
  setupAgain: 'Reconfigurer',
  close: 'Fermer',
  readDocument: 'Lire',
};

const EN: KioskTexts = {
  kiosk: (name) => `Kiosk ${name}`,
  signIn: 'Sign in',
  orBadge: 'or scan your badge',
  offline: 'No connection. Signing in is not possible right now.',
  typeNumber: 'Type your employee number',
  typeNumberHint: 'The number is on your badge.',
  cancel: 'Cancel',
  next: 'Next',
  typePin: 'Type your PIN',
  typePinHint: 'Six digits. Nobody is watching.',
  hello: (name) => `Hello ${name}`,
  signOut: 'Sign out',
  toConfirm: 'To confirm',
  allDone: "You're all caught up.",
  readAndConfirm: 'Read and confirm',
  recent: 'Recent',
  documents: 'Documents to read',
  back: 'Back',
  postOf: (index, total) => `message ${index} of ${total}`,
  view: 'View',
  confirm: 'Confirm',
  confirmedAt: (time) => `Confirmed at ${time}`,
  confirmedHint: 'Thank you. Your confirmation is recorded.',
  stillThere: 'Are you still there?',
  signOutIn: (seconds) => `You will be signed out in ${seconds} seconds.`,
  imHere: "I'm still here",
  signedOut: 'You are signed out',
  nothingLeft: 'Nothing of yours is left on this device.',
  backIn: (seconds) => `Back to the start screen in ${seconds} seconds`,
  thirtySeconds: '30 seconds left. Confirm what you have open.',
  errors: {
    invalid: 'Employee number or PIN is not right. Try again.',
    locked: (minutes) =>
      `Too many attempts. Try again in ${minutes} minutes or ask your team lead.`,
    notActivated: 'Your account is not activated yet. Ask HR for an activation code.',
  },
  revoked: 'This kiosk is switched off.',
  revokedHint: 'Ask HR to set this device up again.',
  setupTitle: 'This device is not a kiosk yet',
  setupHint: 'Type the eight-character code you got in the admin portal.',
  setupCode: 'Code',
  setup: 'Set up',
  setupFailed: 'This code is not right or has expired.',
  confirmFailed: 'Confirming did not work. Please try again.',
  ready: (name) => `Kiosk ${name} is ready`,
  readyHint: 'Employees can now sign in.',
  setupAgain: 'Set up again',
  close: 'Close',
  readDocument: 'Read',
};

export const KIOSK_TEXTS: Record<KioskLanguage, KioskTexts> = { nl: NL, fr: FR, en: EN };
